import { useEffect, useRef, useState, useCallback } from "react";

export function useScrollReveal() {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  const show = useCallback(() => setIsVisible(true), []);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const check = () => {
      if (!node) return false;
      const r = node.getBoundingClientRect();
      return r.top < window.innerHeight + 50 && r.bottom > -50;
    };

    if (check()) {
      show();
      return;
    }

    let observer;
    if ("IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            show();
            if (observer) observer.disconnect();
            window.removeEventListener("scroll", onScroll);
            clearTimeout(timer);
          }
        },
        { threshold: 0 }
      );
      observer.observe(node);
    }

    const onScroll = () => {
      if (check()) {
        show();
        window.removeEventListener("scroll", onScroll);
        if (observer) observer.disconnect();
        clearTimeout(timer);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    const timer = setTimeout(show, 3000);

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (observer) observer.disconnect();
      clearTimeout(timer);
    };
  }, [show]);

  // Return ref, isVisible, and a ready-made style object
  const revealStyle = {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(24px)",
    transition: "opacity 0.7s cubic-bezier(.22,1,.36,1), transform 0.7s cubic-bezier(.22,1,.36,1)",
  };

  return [ref, isVisible, revealStyle];
}
